import { forwardRef, useContext, useId, useMemo } from "react";
import {
  LabelContext,
  TextContext,
  type GroupProps,
} from "react-aria-components";
import { type PropsWithOptionalTestID } from "@boi/components-core";

import * as S from "./DateInput.styled";
import { useControlledValue } from "./DateInput.hooks";
import DateInputContext from "./DateInputContext";
import { InputWrapper } from "../InputWrapper";
import { InputAlerts, type InputAlertsProps } from "../InputAlerts";
import { InputLabelTopV2, type InputLabelTopV2Props } from "../InputLabelTopV2";
import { type TextInputV2Props } from "../TextInputV2";

export type DateInputProps = PropsWithOptionalTestID &
  Pick<GroupProps, "aria-label" | "role" | "children"> & {
    labelProps?: Omit<InputLabelTopV2Props, "children">;
    alerts?: {
      day?: InputAlertsProps["alerts"];
      month?: InputAlertsProps["alerts"];
      year?: InputAlertsProps["alerts"];
    };
  };

function DateInput(props: DateInputProps) {
  const {
    labelProps,
    alerts,
    children,
    testID = "date-input",
    ...otherProps
  } = props;

  const hasLabel = Boolean(labelProps?.label);
  const hasLabelHint = Boolean(labelProps?.labelHint);

  const labelID = useId();
  const descriptionID = useId();

  const dayErrorMessageID = useId();
  const monthErrorMessageID = useId();
  const yearErrorMessageID = useId();

  const labelContextValue = {
    id: labelID,
    elementType: "span",
  };

  const textContextValue = {
    slots: {
      description: { id: descriptionID },
      dayErrorMessage: { id: dayErrorMessageID },
      monthErrorMessage: { id: monthErrorMessageID },
      yearErrorMessage: { id: yearErrorMessageID },
    },
  };

  const inputWrapperAlerts = useMemo(
    () => (
      <S.InputAlertsContainer>
        {alerts?.day && (
          <InputAlerts slot="dayErrorMessage" alerts={alerts.day} />
        )}

        {alerts?.month && (
          <InputAlerts slot="monthErrorMessage" alerts={alerts.month} />
        )}

        {alerts?.year && (
          <InputAlerts slot="yearErrorMessage" alerts={alerts.year} />
        )}
      </S.InputAlertsContainer>
    ),
    [alerts]
  );

  return (
    <DateInputContext.Provider
      value={{
        dayDescribedBy: dayErrorMessageID,
        monthDescribedBy: monthErrorMessageID,
        yearDescribedBy: yearErrorMessageID,
      }}
    >
      <LabelContext.Provider value={labelContextValue}>
        <TextContext.Provider value={textContextValue}>
          <InputWrapper alert={inputWrapperAlerts}>
            <InputLabelTopV2 {...labelProps}>
              <S.Group
                {...otherProps}
                aria-labelledby={
                  [hasLabel && labelID, hasLabelHint && descriptionID]
                    .filter(Boolean)
                    .join(" ") || undefined
                }
                data-testid={testID}
              >
                {children}
              </S.Group>
            </InputLabelTopV2>
          </InputWrapper>
        </TextContext.Provider>
      </LabelContext.Provider>
    </DateInputContext.Provider>
  );
}

type InputProps = Omit<
  TextInputV2Props,
  "labelProps" | "alerts" | "inputmode" | "maxLength" | "aria-describedby"
>;

DateInput.Day = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const { dayDescribedBy } = useContext(DateInputContext);

  const { value, handleChange } = useControlledValue(props);

  return (
    <S.TwoCharacterTextInput
      aria-label="Day"
      {...props}
      ref={ref}
      value={value}
      onChange={handleChange}
      aria-describedby={dayDescribedBy}
    />
  );
});

DateInput.Day.displayName = "DateInputDay";

DateInput.Month = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const { monthDescribedBy } = useContext(DateInputContext);

  const { value, handleChange } = useControlledValue(props);

  return (
    <S.TwoCharacterTextInput
      aria-label="Month"
      {...props}
      ref={ref}
      value={value}
      onChange={handleChange}
      aria-describedby={monthDescribedBy}
    />
  );
});

DateInput.Month.displayName = "DateInputMonth";

DateInput.Year = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const { yearDescribedBy } = useContext(DateInputContext);

  const { value, handleChange } = useControlledValue(props);

  return (
    <S.FourCharacterTextInput
      aria-label="Year"
      {...props}
      ref={ref}
      value={value}
      onChange={handleChange}
      aria-describedby={yearDescribedBy}
    />
  );
});

DateInput.Year.displayName = "DateInputYear";

export default DateInput;
